import React, { useEffect, useMemo, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { cn } from "../lib/utils";
import {
  dateRangeProgressPercent,
  engagementTimelineProgressPercent,
  formatCycleRangeLabel,
} from "../lib/cycleLabels";

function parseIso(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function parseDateValue(value) {
  if (!value) return undefined;
  const [year, month, day] = String(value).slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return undefined;
  return new Date(year, month - 1, day);
}

function toDateValue(date) {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatProjectTimelineLabel(client, activeCycle) {
  if (client?.engagement_type === "monthly" && activeCycle) {
    return formatCycleRangeLabel(activeCycle) || "—";
  }
  const start = client?.timeline_start ? new Date(client.timeline_start) : null;
  const end = client?.timeline_end ? new Date(client.timeline_end) : null;
  if (!start || Number.isNaN(start.getTime())) {
    if (!end || Number.isNaN(end.getTime())) return "—";
    return end.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  }
  if (!end || Number.isNaN(end.getTime())) {
    return start.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  }
  const sameYear = start.getFullYear() === end.getFullYear();
  const left = start.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
  const right = end.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  return `${left} – ${right}`;
}

function TimelinePeriodSelect({ periods, selectedId, onSelect }) {
  const [open, setOpen] = useState(false);
  if (!periods?.length) return null;
  const selected = periods.find((p) => p.id === selectedId) || periods.find((p) => p.current) || periods[0];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex h-10 min-h-0 max-w-[min(100%,220px)] shrink-0 items-center gap-2 rounded-xl px-3 text-left text-[11px] uppercase tracking-[0.14em] text-[var(--t-meta)] hover:text-[var(--t-text)]"
          data-testid="home-timeline-select"
        >
          <span className="shrink-0">Timeline</span>
          <span
            className={`min-w-0 flex-1 truncate text-sm !normal-case tracking-normal !font-light ${
              selected?.current ? "text-[var(--t-accent)]" : "text-[var(--t-text)]"
            }`}
          >
            {selected?.label || "Current"}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        sideOffset={6}
        className="z-[60] w-auto min-w-[14rem] neo-panel p-2 text-[var(--t-text)] t-glass"
        data-testid="home-timeline-menu"
      >
        <div className="flex max-h-[280px] flex-col gap-1 overflow-auto">
          {periods.map((period) => {
            const isSelected = period.id === selected?.id;
            return (
              <button
                key={period.id}
                type="button"
                className={cn(
                  "rounded-[var(--t-event-pill-radius)] px-3 py-2 text-left text-[10px] font-normal uppercase tracking-[0.16em] transition-colors hover:bg-black/[0.04]",
                  isSelected &&
                    "border border-[var(--t-accent)] bg-[var(--t-accent)]/5 text-[var(--t-accent)]",
                  !isSelected && period.current && "text-[var(--t-accent)]",
                )}
                onMouseDown={(e) => {
                  e.preventDefault();
                  onSelect?.(period);
                  setOpen(false);
                }}
                data-testid={`home-timeline-option-${period.id}`}
              >
                {period.label}
                {period.current ? " · Now" : ""}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

const TIMELINE_PANEL_CLASS =
  "mt-3 w-full overflow-hidden rounded-[var(--t-event-pill-radius)] border border-[var(--t-border)] p-0 !shadow-none text-[var(--t-text)] t-glass";

/** Project timeline card — date range + elapsed bar + past timeline picker (+ admin edit). */
export default function ProjectTimelineCard({
  client,
  activeCycle = null,
  cycles = [],
  periods = [],
  selectedPeriod = null,
  onSelectPeriod,
  canEdit = false,
  onSaveTimeline,
  className = "",
}) {
  const reduce = useReducedMotion();
  const viewing = selectedPeriod || periods.find((p) => p.current) || null;
  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft] = useState({ from: undefined, to: undefined });
  const [saving, setSaving] = useState(false);

  const timelineLabel = viewing?.label
    ? viewing.label
    : client?.engagement_type === "monthly" && activeCycle
      ? formatCycleRangeLabel(activeCycle) || formatProjectTimelineLabel(client, activeCycle)
      : formatProjectTimelineLabel(client, activeCycle);

  const timelineProgress = useMemo(() => {
    if (viewing?.timeline_start || viewing?.timeline_end) {
      const pct = dateRangeProgressPercent(
        parseIso(viewing.timeline_start),
        parseIso(viewing.timeline_end),
      );
      return { pct: pct ?? 0, hasWindow: pct != null };
    }
    const pct = engagementTimelineProgressPercent(client, activeCycle, cycles);
    return { pct: pct ?? 0, hasWindow: pct != null };
  }, [activeCycle, client, cycles, viewing]);

  const isCurrent = viewing ? Boolean(viewing.current) : true;
  const editable = Boolean(canEdit && onSaveTimeline && isCurrent);

  useEffect(() => {
    if (!editOpen) return;
    const start = viewing?.timeline_start || client?.timeline_start;
    const end = viewing?.timeline_end || client?.timeline_end;
    setDraft({
      from: parseDateValue(start),
      to: parseDateValue(end),
    });
  }, [editOpen, viewing?.timeline_start, viewing?.timeline_end, client?.timeline_start, client?.timeline_end]);

  useEffect(() => {
    if (!editOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setEditOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editOpen]);

  const applyTimeline = async () => {
    if (!draft?.from || saving) return;
    setSaving(true);
    try {
      await onSaveTimeline?.({
        timeline_start: toDateValue(draft.from),
        timeline_end: draft.to ? toDateValue(draft.to) : "",
      });
      setEditOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const labelNode = (
    <div
      className={`font-heading text-xl tracking-tight !font-light md:text-2xl ${
        isCurrent ? "text-[var(--t-accent)]" : ""
      }`}
    >
      {timelineLabel}
    </div>
  );

  return (
    <div className={`neo-panel p-4 sm:p-5 ${className}`.trim()} data-testid="plan-timeline">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="label-eyebrow mb-2">Project timeline</div>
          {editable ? (
            <button
              type="button"
              className="group text-left"
              data-testid="home-timeline-edit"
              aria-label="Edit project timeline"
              aria-expanded={editOpen}
              onClick={() => setEditOpen((v) => !v)}
            >
              <span
                className={cn(
                  "inline-block rounded px-1 -mx-1 transition-colors",
                  "group-hover:bg-[rgba(0,30,255,0.04)] group-hover:ring-1 group-hover:ring-[var(--t-accent)]/30",
                  editOpen && "bg-[rgba(0,30,255,0.04)] ring-1 ring-[var(--t-accent)]/35",
                )}
              >
                {labelNode}
              </span>
              <span
                className={cn(
                  "mt-1 block text-[10px] uppercase tracking-[0.14em] text-[var(--t-meta)] transition-opacity",
                  editOpen ? "opacity-100" : "opacity-0 group-hover:opacity-100",
                )}
              >
                {editOpen ? "Close" : "Edit dates"}
              </span>
            </button>
          ) : (
            labelNode
          )}
        </div>
        {periods.length > 1 ? (
          <TimelinePeriodSelect
            periods={periods}
            selectedId={viewing?.id}
            onSelect={onSelectPeriod}
          />
        ) : null}
      </div>

      {editable && editOpen ? (
        <div className={TIMELINE_PANEL_CLASS} data-testid="home-timeline-edit-panel">
          <div className="flex items-center justify-between gap-3 border-b border-[var(--t-border)] p-3">
            <div className="label-eyebrow">Timeline</div>
            <button
              type="button"
              className="t-btn-ghost px-2 py-1 text-xs"
              onClick={() => setDraft({ from: undefined, to: undefined })}
              data-testid="home-timeline-edit-clear"
            >
              Clear
            </button>
          </div>
          <Calendar
            mode="range"
            selected={draft}
            onSelect={(next) => setDraft(next || { from: undefined, to: undefined })}
            numberOfMonths={1}
            initialFocus
            className="w-full min-w-0 p-3 sm:p-4 [&_.rdp-month]:w-full [&_.rdp-table]:w-full"
          />
          <div className="flex justify-end border-t border-[var(--t-border)] p-3">
            <button
              type="button"
              className="neo-cta"
              disabled={!draft?.from || saving}
              onClick={applyTimeline}
              data-testid="home-timeline-edit-apply"
            >
              {saving ? "Saving…" : "Apply"}
            </button>
          </div>
        </div>
      ) : null}

      {timelineProgress.hasWindow ? (
        <div className="neo-well mt-4 h-2 overflow-hidden rounded-full">
          <motion.div
            className="h-full rounded-full bg-[var(--t-accent)]"
            initial={reduce ? false : { width: 0 }}
            animate={{ width: `${timelineProgress.pct}%` }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            data-testid="plan-timeline-progress"
          />
        </div>
      ) : null}
    </div>
  );
}
